import { batch } from 'react-redux';
import { AsyncAction, CommanderThunkAction, WithAccountId } from '@mews-commander/types';
import {
    AccountExtent,
    AccountResult,
    GetMultipleAccountParameters,
    MultipleAccountDeleteParameters,
    MultipleAccountUpdateParameters,
} from '@mews-commander/data-access';
import { createFetchAction } from '@mews-commander/utils';
import prefix from '../prefix';
import { setFiles } from '../ducks/files';
import { setCustomers } from '../ducks/customers';
import { setCompanies } from '../ducks/companies';
import { setAddresses } from '../ducks/addresses';
import { setIdentityDocuments } from '../ducks/identityDocuments';
import { dispatchScopedGroupedEntityActions } from '../ducks/utils';
import { setCreditLimits } from '../ducks/creditLimits';
import { setLegalEntityIdentifiers } from '../ducks/legalEntityIdentifiers';
import { setProfiles } from '../ducks/profiles';
import { setCustomerUsers } from '../ducks/customerUsers';
import { getAssociatedAccountIdsByBillsIds, getOwnerIdsByBillsIds } from '../ducks/bills';

export const NAME = 'reservationGroups';

const ACCOUNTS_FETCH = prefix('ACCOUNTS_FETCH');
const ACCOUNTS_UPDATE = prefix('ACCOUNTS_UPDATE');
const ACCOUNTS_DELETE = prefix('ACCOUNTS_DELETE');

const setAccountEntities =
    ({
        customers,
        companies,
        files,
        addresses,
        identityDocuments,
        creditLimits,
        legalEntityIdentifiers,
        profiles,
        users,
    }: AccountResult): CommanderThunkAction =>
    (dispatch) => {
        batch(() => {
            if (customers?.length) {
                dispatch(setCustomers(customers));
            }

            if (profiles?.length) {
                dispatch(setProfiles(profiles));
            }

            if (companies?.length) {
                dispatch(setCompanies(companies));
            }

            if (creditLimits?.length) {
                dispatch(setCreditLimits(creditLimits));
            }

            if (files?.length) {
                dispatch(
                    dispatchScopedGroupedEntityActions({
                        entities: files,
                        action: setFiles,
                        scopeName: 'accountId',
                    })
                );
            }

            if (addresses?.length) {
                dispatch(
                    dispatchScopedGroupedEntityActions({
                        entities: addresses,
                        action: setAddresses,
                        scopeName: 'owner.value.id',
                    })
                );
            }

            if (identityDocuments?.length) {
                dispatch(
                    dispatchScopedGroupedEntityActions({
                        entities: identityDocuments,
                        action: setIdentityDocuments,
                        scopeName: 'customerId',
                    })
                );
            }

            if (legalEntityIdentifiers?.length) {
                dispatch(setLegalEntityIdentifiers(legalEntityIdentifiers));
            }

            if (users?.length) {
                dispatch(setCustomerUsers(users));
            }
        });
    };

type FetchParams = Pick<GetMultipleAccountParameters, 'accountIds'> & { extent?: AccountExtent };

const fetch =
    ({ accountIds, extent = {} }: FetchParams): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.accounts.getAll({ accountIds, extent }).then((response) => {
            dispatch(setAccountEntities(response));
        });

        return dispatch(createFetchAction(ACCOUNTS_FETCH, promise));
    };

type UpdateParams = Pick<MultipleAccountUpdateParameters, 'accountUpdates'>;

const update =
    ({ accountUpdates }: UpdateParams): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.accounts.update({ accountUpdates }).then((response) => {
            dispatch(setAccountEntities(response));
        });

        return dispatch(createFetchAction(ACCOUNTS_UPDATE, promise));
    };

type DeleteAccountsParams = Pick<MultipleAccountDeleteParameters, 'accountIds'>;

export const deleteAccounts =
    ({ accountIds }: DeleteAccountsParams): CommanderThunkAction<AsyncAction> =>
    async (dispatch, getState, { repositories }) => {
        const promise = repositories.accounts.delete({ accountIds });

        return dispatch(createFetchAction(ACCOUNTS_DELETE, promise));
    };

export const fetchAccountById = ({ accountId, extent }: WithAccountId & { extent?: AccountExtent }) =>
    fetch({ accountIds: [accountId], extent });
export const fetchAccountsByIds = ({ accountIds, extent }: FetchParams) => fetch({ accountIds, extent });
export const updateAccount = ({ accountUpdates }: UpdateParams) => update({ accountUpdates });
export const deleteAccount = ({ accountId }: WithAccountId) => deleteAccounts({ accountIds: [accountId] });

export const fetchAccountsByBillIds =
    ({ billIds }: { billIds: string[] }): CommanderThunkAction =>
    async (dispatch, getState) => {
        const state = getState();

        const associatedAccountIds = getAssociatedAccountIdsByBillsIds(state, { ids: billIds });
        const ownerIds = getOwnerIdsByBillsIds(state, { ids: billIds });
        const allIds = [...associatedAccountIds, ...ownerIds];

        if (allIds?.length) {
            return dispatch(fetchAccountsByIds({ accountIds: allIds }));
        }
    };
